import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useSearchParams, useNavigate, useLocation, useInRouterContext } from 'react-router-dom';

import { useCurrentUserContext } from '@openclassrooms/data-connect-web/users';

import RedirectToSignin from 'Modules/errors/components/RedirectToSignin';
import FileDownloadDialog from './FileDownloadDialog';

const URL_GET_PARAM = 'protectedFile';
const CONFIRMATION_GET_PARAM = 'showConfirmation';
const AUTH_CONTEXT_GET_PARAM = 'context';

export default function ProtectedFileDownloadHandler({ children }: { children: ReactNode }) {
  const { isConnected, isLoading } = useCurrentUserContext();
  const isAnonymous = !isConnected;
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isRouterContext = useInRouterContext();

  const context = searchParams.get(AUTH_CONTEXT_GET_PARAM);
  const protectedFileUrl = searchParams.get(URL_GET_PARAM);
  const shouldShowDownloadConfirmation =
    searchParams.has(CONFIRMATION_GET_PARAM) && Boolean(protectedFileUrl);

  const [isDialogOpen, setIsDialogOpen] = useState(shouldShowDownloadConfirmation);

  const resetDownload = useCallback(() => {
    setIsDialogOpen(false);
    const currentUrl = new URL(document.location.toString());
    currentUrl.searchParams.delete(URL_GET_PARAM);
    currentUrl.searchParams.delete(CONFIRMATION_GET_PARAM);

    if (isRouterContext) {
      navigate(location.pathname, { state: { preventScrollReset: true } });
    }
  }, [isRouterContext, location.pathname, navigate]);

  const openFile = useCallback(() => {
    if (!protectedFileUrl) {
      return;
    }

    if (!protectedFileUrl.startsWith('https://static.oc-static.com/')) {
      resetDownload();

      return;
    }

    if (!window.open(protectedFileUrl, '_blank')) {
      window.location.assign(protectedFileUrl);
    }

    resetDownload();
  }, [protectedFileUrl, resetDownload]);

  useEffect(() => {
    if (!shouldShowDownloadConfirmation && !isLoading && !isAnonymous) {
      openFile();
    }
  }, [isLoading, isAnonymous, openFile, shouldShowDownloadConfirmation]);

  const handleDialogConfirm = () => {
    openFile();
  };

  const handleDialogCancel = () => {
    resetDownload();
  };

  if (!protectedFileUrl) {
    return <>{children}</>;
  }

  if (isLoading) {
    return <>{children}</>;
  }

  if (isAnonymous) {
    const targetPathUrl = new URL(window.location.toString());
    targetPathUrl.searchParams.append(CONFIRMATION_GET_PARAM, 'true');
    return <RedirectToSignin authContext={context} targetPath={targetPathUrl.toString()} />;
  }

  return (
    <>
      {isDialogOpen && (
        <FileDownloadDialog onConfirm={handleDialogConfirm} onCancel={handleDialogCancel} />
      )}
      {children}
    </>
  );
}
